import { useMutation, useQueryClient } from "@tanstack/react-query";
import { isAxiosError } from "axios";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { api } from "../../api/axios";
import { apiUrl } from "../../config/backendFeatures";
import { useAuthStore } from "../../store/useAuthStore";
import type { ConversationDto } from "../contract/chat.events";
import { chatKeys } from "../lib/queryKeys";
import { getChatTransport } from "../socket/transport";
import { useChatOutbox } from "../store/outboxStore";
import { useChatPopup } from "../store/popupStore";

/** Get-or-create the user's support chat with the official Turaq.kg account. */
export async function openSupportConversation(): Promise<ConversationDto> {
  const { data } = await api.post(apiUrl("/api/chat/conversations/support"));
  return data as ConversationDto;
}

/**
 * A request nobody else can take on the site (no CRM, no listing to chat on)
 * is SENT to the Turaq.kg support chat instead of WhatsApp. Returns the
 * conversation, or null for guests / when the chat can't be opened.
 */
export async function sendToSupport(
  text: string,
  queryClient: ReturnType<typeof useQueryClient>,
  { openPopup = true }: { openPopup?: boolean } = {},
): Promise<ConversationDto | null> {
  if (!useAuthStore.getState().isAuthenticated) return null;
  try {
    const conversation = await openSupportConversation();
    queryClient.setQueryData(chatKeys.conversation(conversation.id), conversation);
    useChatOutbox.getState().enqueue({
      clientMsgId: crypto.randomUUID(),
      conversationId: conversation.id,
      body: text.trim(),
      replyToId: null,
      attachmentIds: [],
      createdAt: new Date().toISOString(),
    });
    getChatTransport()?.kickOutbox();
    if (openPopup) useChatPopup.getState().open(conversation.id);
    return conversation;
  } catch {
    return null;
  }
}

/**
 * «Колдоо кызматына кайрылуу» / «Написать Turaq.kg»: opens a private chat
 * with Turaq.kg (any question). Guests sign in first; the official account
 * itself lands in its inbox, where these chats arrive.
 */
export function useOpenSupportChat() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const openPopup = useChatPopup((s) => s.open);

  const mutation = useMutation({
    mutationFn: openSupportConversation,
    onSuccess: (conversation) => {
      queryClient.setQueryData(chatKeys.conversation(conversation.id), conversation);
      void queryClient.invalidateQueries({ queryKey: chatKeys.lists() });
      openPopup(conversation.id);
    },
    onError: (error) => {
      const status = isAxiosError(error) ? error.response?.status : undefined;
      // The official account has no support chat with itself — its inbox is where they arrive.
      if (status === 400) return navigate("/messages");
      // Backend without the support endpoint yet: the channel is the next best place.
      if (status === 404) return navigate("/messages/official");
      toast.error(t("support.failed", "Чат ачылган жок. Кайра аракет кылыңыз."));
    },
  });

  const open = () => {
    if (!isAuthenticated) {
      navigate("/login", { state: { from: { pathname: location.pathname, search: location.search, hash: location.hash } } });
      return;
    }
    if (!mutation.isPending) mutation.mutate();
  };

  return { open, pending: mutation.isPending };
}
